<template>
  <div class="address-form">
    <h3>{{ isEdit ? '编辑地址' : '新增地址' }}</h3>
    
    <form @submit.prevent="handleSubmit">
      <div class="form-group">
        <label for="receiverName">收货人 <span class="required">*</span></label>
        <input 
          type="text" 
          id="receiverName" 
          v-model="form.receiverName" 
          required
          placeholder="请输入收货人姓名"
          maxlength="20"
        >
      </div>
      
      <div class="form-group">
        <label for="phone">手机号 <span class="required">*</span></label>
        <input 
          type="tel" 
          id="phone" 
          v-model="form.phone" 
          required
          placeholder="请输入11位手机号"
          maxlength="11"
        >
        <span v-if="phoneError" class="error-message">{{ phoneError }}</span>
      </div>
      
      <div class="form-group">
        <label>省市区 <span class="required">*</span></label>
        <div class="region-selects">
          <select v-model="form.province" required @change="onProvinceChange">
            <option value="">请选择省份</option>
            <option v-for="province in provinces" :key="province" :value="province">
              {{ province }}
            </option>
          </select>
          <select v-model="form.city" required @change="onCityChange" :disabled="!form.province">
            <option value="">请选择城市</option>
            <option v-for="city in cities" :key="city" :value="city">
              {{ city }}
            </option>
          </select>
          <select v-model="form.district" required :disabled="!form.city">
            <option value="">请选择区县</option>
            <option v-for="district in districts" :key="district" :value="district">
              {{ district }}
            </option>
          </select>
        </div>
      </div>
      
      <div class="form-group">
        <label for="detailAddress">详细地址 <span class="required">*</span></label>
        <textarea 
          id="detailAddress" 
          v-model="form.detailAddress" 
          required
          placeholder="请输入详细地址（如街道、门牌号等）"
          maxlength="100"
          rows="2"
        ></textarea>
      </div>
      
      <div class="form-group">
        <label for="postalCode">邮编</label>
        <input 
          type="text" 
          id="postalCode" 
          v-model="form.postalCode"
          placeholder="请输入邮编（选填）"
          maxlength="6"
        >
      </div>
      
      <div class="form-actions">
        <button type="button" class="btn" @click="$emit('cancel')">取消</button>
        <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
          {{ isSubmitting ? '保存中...' : '保存' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { ref, reactive } from 'vue';
import { useAutoSave } from '../composables/useAutoSave';

export default {
  name: 'AddressForm',
  props: {
    address: {
      type: Object,
      default: null
    },
    isEdit: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const form = reactive({
      receiverName: '',
      phone: '',
      province: '',
      city: '',
      district: '',
      detailAddress: '',
      postalCode: ''
    });
    
    const autoSaveKey = 'address_form_auto_save';
    
    const { 
      autoSaveStatus, 
      lastSavedTime, 
      clearAutoSave 
    } = useAutoSave(autoSaveKey, ref(form), null, {
      autoLoad: true,
      loadCallback: (savedData) => {
        if (!this?.isEdit) {
          Object.assign(form, savedData);
        }
      }
    });
    
    return {
      form,
      autoSaveStatus,
      lastSavedTime,
      clearAutoSave
    };
  },
  data() {
    return {
      phoneError: '',
      isSubmitting: false,
      regionData: {
        '北京市': {
          '北京市': ['东城区', '西城区', '朝阳区', '丰台区', '石景山区', '海淀区', '门头沟区', '房山区', '通州区', '顺义区', '昌平区', '大兴区', '怀柔区', '平谷区', '密云区', '延庆区']
        },
        '上海市': {
          '上海市': ['黄浦区', '徐汇区', '长宁区', '静安区', '普陀区', '虹口区', '杨浦区', '闵行区', '宝山区', '嘉定区', '浦东新区', '金山区', '松江区', '青浦区', '奉贤区', '崇明区']
        },
        '广东省': {
          '广州市': ['越秀区', '海珠区', '荔湾区', '天河区', '白云区', '黄埔区', '番禺区', '花都区', '南沙区', '从化区', '增城区'],
          '深圳市': ['罗湖区', '福田区', '南山区', '宝安区', '龙岗区', '盐田区', '龙华区', '坪山区', '光明区'],
          '东莞市': ['东城街道', '南城街道', '万江街道', '莞城街道', '石碣镇', '石龙镇', '茶山镇', '石排镇', '企石镇', '横沥镇', '桥头镇', '谢岗镇', '东坑镇', '常平镇'],
          '佛山市': ['禅城区', '南海区', '顺德区', '三水区', '高明区']
        },
        '江苏省': {
          '南京市': ['玄武区', '秦淮区', '建邺区', '鼓楼区', '浦口区', '栖霞区', '雨花台区', '江宁区', '六合区', '溧水区', '高淳区'],
          '苏州市': ['虎丘区', '吴中区', '相城区', '姑苏区', '吴江区', '昆山市', '太仓市', '常熟市', '张家港市'],
          '无锡市': ['锡山区', '惠山区', '滨湖区', '梁溪区', '新吴区', '江阴市', '宜兴市']
        },
        '浙江省': {
          '杭州市': ['上城区', '拱墅区', '西湖区', '滨江区', '萧山区', '余杭区', '富阳区', '临安区', '临平区', '钱塘区', '桐庐县', '淳安县', '建德市'],
          '宁波市': ['海曙区', '江北区', '北仑区', '镇海区', '鄞州区', '奉化区', '余姚市', '慈溪市', '象山县', '宁海县'],
          '温州市': ['鹿城区', '龙湾区', '瓯海区', '洞头区', '永嘉县', '平阳县', '苍南县', '文成县', '泰顺县', '瑞安市', '乐清市']
        },
        '四川省': {
          '成都市': ['锦江区', '青羊区', '金牛区', '武侯区', '成华区', '龙泉驿区', '青白江区', '新都区', '温江区', '双流区', '郫都区', '新津区'],
          '绵阳市': ['涪城区', '游仙区', '安州区', '三台县', '盐亭县', '梓潼县', '北川羌族自治县', '平武县', '江油市'],
          '德阳市': ['旌阳区', '罗江区', '中江县', '广汉市', '什邡市', '绵竹市']
        },
        '湖北省': {
          '武汉市': ['江岸区', '江汉区', '硚口区', '汉阳区', '武昌区', '青山区', '洪山区', '东西湖区', '汉南区', '蔡甸区', '江夏区', '黄陂区', '新洲区'],
          '宜昌市': ['西陵区', '伍家岗区', '点军区', '猇亭区', '夷陵区', '远安县', '兴山县', '秭归县', '长阳土家族自治县', '五峰土家族自治县', '宜都市', '当阳市', '枝江市'],
          '襄阳市': ['襄城区', '樊城区', '襄州区', '南漳县', '谷城县', '保康县', '老河口市', '枣阳市', '宜城市']
        },
        '湖南省': {
          '长沙市': ['芙蓉区', '天心区', '岳麓区', '开福区', '雨花区', '望城区', '长沙县', '浏阳市', '宁乡市'],
          '株洲市': ['荷塘区', '芦淞区', '石峰区', '天元区', '渌口区', '攸县', '茶陵县', '炎陵县', '醴陵市'],
          '湘潭市': ['雨湖区', '岳塘区', '湘潭县', '湘乡市', '韶山市']
        },
        '河南省': {
          '郑州市': ['中原区', '二七区', '管城回族区', '金水区', '上街区', '惠济区', '中牟县', '巩义市', '荥阳市', '新密市', '新郑市', '登封市'],
          '洛阳市': ['老城区', '西工区', '瀍河回族区', '涧西区', '吉利区', '洛龙区', '孟津区', '新安县', '栾川县', '嵩县', '汝阳县', '宜阳县', '洛宁县', '伊川县', '偃师区'],
          '开封市': ['龙亭区', '顺河回族区', '鼓楼区', '禹王台区', '祥符区', '杞县', '通许县', '尉氏县', '兰考县']
        },
        '山东省': {
          '济南市': ['历下区', '市中区', '槐荫区', '天桥区', '历城区', '长清区', '章丘区', '济阳区', '莱芜区', '钢城区', '平阴县', '商河县'],
          '青岛市': ['市南区', '市北区', '黄岛区', '崂山区', '李沧区', '城阳区', '即墨区', '胶州市', '平度市', '莱西市'],
          '烟台市': ['芝罘区', '福山区', '牟平区', '莱山区', '蓬莱区', '龙口市', '莱阳市', '莱州市', '招远市', '栖霞市', '海阳市']
        },
        '福建省': {
          '福州市': ['鼓楼区', '台江区', '仓山区', '马尾区', '晋安区', '长乐区', '闽侯县', '连江县', '罗源县', '闽清县', '永泰县', '平潭县', '福清市'],
          '厦门市': ['思明区', '海沧区', '湖里区', '集美区', '同安区', '翔安区'],
          '泉州市': ['鲤城区', '丰泽区', '洛江区', '泉港区', '惠安县', '安溪县', '永春县', '德化县', '金门县', '石狮市', '晋江市', '南安市']
        },
        '陕西省': {
          '西安市': ['新城区', '碑林区', '莲湖区', '灞桥区', '未央区', '雁塔区', '阎良区', '临潼区', '长安区', '高陵区', '鄠邑区', '蓝田县', '周至县'],
          '宝鸡市': ['渭滨区', '金台区', '陈仓区', '凤翔区', '岐山县', '扶风县', '眉县', '陇县', '千阳县', '麟游县', '凤县', '太白县'],
          '咸阳市': ['秦都区', '杨陵区', '渭城区', '三原县', '泾阳县', '乾县', '礼泉县', '永寿县', '长武县', '旬邑县', '淳化县', '武功县', '兴平市', '彬州市']
        },
        '辽宁省': {
          '沈阳市': ['和平区', '沈河区', '大东区', '皇姑区', '铁西区', '苏家屯区', '浑南区', '沈北新区', '于洪区', '辽中区', '康平县', '法库县', '新民市'],
          '大连市': ['中山区', '西岗区', '沙河口区', '甘井子区', '旅顺口区', '金州区', '普兰店区', '长海县', '瓦房店市', '庄河市'],
          '鞍山市': ['铁东区', '铁西区', '立山区', '千山区', '台安县', '岫岩满族自治县', '海城市']
        },
        '天津市': {
          '天津市': ['和平区', '河东区', '河西区', '南开区', '河北区', '红桥区', '东丽区', '西青区', '津南区', '北辰区', '武清区', '宝坻区', '滨海新区', '宁河区', '静海区', '蓟州区']
        },
        '重庆市': {
          '重庆市': ['万州区', '涪陵区', '渝中区', '大渡口区', '江北区', '沙坪坝区', '九龙坡区', '南岸区', '北碚区', '渝北区', '巴南区', '黔江区', '长寿区', '江津区', '合川区', '永川区', '南川区', '綦江区', '大足区', '璧山区', '铜梁区', '潼南区', '荣昌区']
        },
        '河北省': {
          '石家庄市': ['长安区', '桥西区', '新华区', '井陉矿区', '裕华区', '藁城区', '鹿泉区', '栾城区', '井陉县', '正定县', '行唐县', '灵寿县', '高邑县', '深泽县', '赞皇县', '无极县', '平山县', '元氏县', '赵县', '辛集市', '晋州市', '新乐市'],
          '唐山市': ['路南区', '路北区', '古冶区', '开平区', '丰南区', '丰润区', '曹妃甸区', '滦南县', '乐亭县', '迁西县', '玉田县', '遵化市', '迁安市', '滦州市'],
          '保定市': ['竞秀区', '莲池区', '满城区', '清苑区', '徐水区', '涞水县', '阜平县', '定兴县', '唐县', '高阳县', '容城县', '涞源县', '望都县', '安新县', '易县', '曲阳县', '蠡县', '顺平县', '博野县', '雄县', '涿州市', '定州市', '安国市', '高碑店市']
        },
        '安徽省': {
          '合肥市': ['瑶海区', '庐阳区', '蜀山区', '包河区', '长丰县', '肥东县', '肥西县', '庐江县', '巢湖市'],
          '芜湖市': ['镜湖区', '弋江区', '鸠江区', '三山区', '芜湖县', '繁昌区', '南陵县', '无为市'],
          '蚌埠市': ['龙子湖区', '蚌山区', '禹会区', '淮上区', '怀远县', '五河县', '固镇县']
        },
        '江西省': {
          '南昌市': ['东湖区', '西湖区', '青云谱区', '青山湖区', '新建区', '红谷滩区', '南昌县', '安义县', '进贤县'],
          '九江市': ['濂溪区', '浔阳区', '柴桑区', '武宁县', '修水县', '永修县', '德安县', '都昌县', '湖口县', '彭泽县', '瑞昌市', '共青城市', '庐山市'],
          '景德镇市': ['昌江区', '珠山区', '浮梁县', '乐平市']
        },
        '黑龙江省': {
          '哈尔滨市': ['道里区', '南岗区', '道外区', '平房区', '松北区', '香坊区', '呼兰区', '阿城区', '双城区', '依兰县', '方正县', '宾县', '巴彦县', '木兰县', '通河县', '延寿县', '尚志市', '五常市'],
          '齐齐哈尔市': ['龙沙区', '建华区', '铁锋区', '昂昂溪区', '富拉尔基区', '碾子山区', '梅里斯达斡尔族区', '龙江县', '依安县', '泰来县', '甘南县', '富裕县', '克山县', '克东县', '拜泉县', '讷河市'],
          '牡丹江市': ['东安区', '阳明区', '爱民区', '西安区', '林口县', '绥芬河市', '海林市', '宁安市', '穆棱市', '东宁市']
        },
        '吉林省': {
          '长春市': ['南关区', '宽城区', '朝阳区', '二道区', '绿园区', '双阳区', '九台区', '农安县', '榆树市', '德惠市', '公主岭市'],
          '吉林市': ['昌邑区', '龙潭区', '船营区', '丰满区', '永吉县', '蛟河市', '桦甸市', '舒兰市', '磐石市'],
          '四平市': ['铁西区', '铁东区', '梨树县', '伊通满族自治县', '公主岭市', '双辽市']
        },
        '山西省': {
          '太原市': ['小店区', '迎泽区', '杏花岭区', '尖草坪区', '万柏林区', '晋源区', '清徐县', '阳曲县', '娄烦县', '古交市'],
          '大同市': ['新荣区', '平城区', '云冈区', '云州区', '阳高县', '天镇县', '广灵县', '灵丘县', '浑源县', '左云县'],
          '晋中市': ['榆次区', '太谷区', '榆社县', '左权县', '和顺县', '昔阳县', '寿阳县', '祁县', '平遥县', '灵石县', '介休市']
        },
        '贵州省': {
          '贵阳市': ['南明区', '云岩区', '花溪区', '乌当区', '白云区', '观山湖区', '开阳县', '息烽县', '修文县', '清镇市'],
          '遵义市': ['红花岗区', '汇川区', '播州区', '桐梓县', '绥阳县', '正安县', '道真仡佬族苗族自治县', '务川仡佬族苗族自治县', '凤冈县', '湄潭县', '余庆县', '习水县', '赤水市', '仁怀市'],
          '六盘水市': ['钟山区', '六枝特区', '水城区', '盘州市']
        },
        '云南省': {
          '昆明市': ['五华区', '盘龙区', '官渡区', '西山区', '东川区', '呈贡区', '晋宁区', '富民县', '宜良县', '石林彝族自治县', '嵩明县', '禄劝彝族苗族自治县', '寻甸回族彝族自治县', '安宁市'],
          '大理白族自治州': ['大理市', '漾濞彝族自治县', '祥云县', '宾川县', '弥渡县', '南涧彝族自治县', '巍山彝族回族自治县', '永平县', '云龙县', '洱源县', '剑川县', '鹤庆县'],
          '丽江市': ['古城区', '玉龙纳西族自治县', '永胜县', '华坪县', '宁蒗彝族自治县']
        },
        '甘肃省': {
          '兰州市': ['城关区', '七里河区', '西固区', '安宁区', '红古区', '永登县', '皋兰县', '榆中县'],
          '天水市': ['秦州区', '麦积区', '清水县', '秦安县', '甘谷县', '武山县', '张家川回族自治县'],
          '酒泉市': ['肃州区', '金塔县', '瓜州县', '肃北蒙古族自治县', '阿克塞哈萨克族自治县', '玉门市', '敦煌市']
        },
        '海南省': {
          '海口市': ['秀英区', '龙华区', '琼山区', '美兰区'],
          '三亚市': ['海棠区', '吉阳区', '天涯区', '崖州区'],
          '三沙市': ['西沙区', '南沙区'],
          '儋州市': ['那大镇', '和庆镇', '南丰镇', '大成镇', '雅星镇', '兰洋镇', '光村镇', '木棠镇', '海头镇', '峨蔓镇', '王五镇', '白马井镇', '中和镇', '排浦镇', '东成镇', '新州镇']
        },
        '青海省': {
          '西宁市': ['城东区', '城中区', '城西区', '城北区', '湟中区', '大通回族土族自治县', '湟源县'],
          '海东市': ['乐都区', '平安区', '民和回族土族自治县', '互助土族自治县', '化隆回族自治县', '循化撒拉族自治县']
        },
        '内蒙古自治区': {
          '呼和浩特市': ['新城区', '回民区', '玉泉区', '赛罕区', '土默特左旗', '托克托县', '和林格尔县', '清水河县', '武川县'],
          '包头市': ['东河区', '昆都仑区', '青山区', '石拐区', '白云鄂博矿区', '九原区', '土默特右旗', '固阳县', '达尔罕茂明安联合旗'],
          '鄂尔多斯市': ['东胜区', '康巴什区', '达拉特旗', '准格尔旗', '鄂托克前旗', '鄂托克旗', '杭锦旗', '乌审旗', '伊金霍洛旗']
        },
        '广西壮族自治区': {
          '南宁市': ['兴宁区', '青秀区', '江南区', '西乡塘区', '良庆区', '邕宁区', '武鸣区', '隆安县', '马山县', '上林县', '宾阳县', '横州市'],
          '柳州市': ['城中区', '鱼峰区', '柳南区', '柳北区', '柳江区', '柳城县', '鹿寨县', '融安县', '融水苗族自治县', '三江侗族自治县'],
          '桂林市': ['秀峰区', '叠彩区', '象山区', '七星区', '雁山区', '临桂区', '阳朔县', '灵川县', '全州县', '兴安县', '永福县', '灌阳县', '龙胜各族自治县', '资源县', '平乐县', '恭城瑶族自治县', '荔浦市']
        },
        '西藏自治区': {
          '拉萨市': ['城关区', '堆龙德庆区', '达孜区', '林周县', '当雄县', '尼木县', '曲水县', '墨竹工卡县'],
          '日喀则市': ['桑珠孜区', '南木林县', '江孜县', '定日县', '萨迦县', '拉孜县', '昂仁县', '谢通门县', '白朗县', '仁布县', '康马县', '定结县', '仲巴县', '亚东县', '吉隆县', '聂拉木县', '萨嘎县', '岗巴县'],
          '昌都市': ['卡若区', '江达县', '贡觉县', '类乌齐县', '丁青县', '察雅县', '八宿县', '左贡县', '芒康县', '洛隆县', '边坝县']
        },
        '宁夏回族自治区': {
          '银川市': ['兴庆区', '西夏区', '金凤区', '永宁县', '贺兰县', '灵武市'],
          '石嘴山市': ['大武口区', '惠农区', '平罗县'],
          '吴忠市': ['利通区', '红寺堡区', '盐池县', '同心县', '青铜峡市']
        },
        '新疆维吾尔自治区': {
          '乌鲁木齐市': ['天山区', '沙依巴克区', '新市区', '水磨沟区', '头屯河区', '达坂城区', '米东区', '乌鲁木齐县'],
          '克拉玛依市': ['独山子区', '克拉玛依区', '白碱滩区', '乌尔禾区'],
          '吐鲁番市': ['高昌区', '鄯善县', '托克逊县']
        },
        '香港特别行政区': {
          '香港': ['中西区', '湾仔区', '东区', '南区', '油尖旺区', '深水埗区', '九龙城区', '黄大仙区', '观塘区', '荃湾区', '屯门区', '元朗区', '北区', '大埔区', '西贡区', '沙田区', '葵青区', '离岛区']
        },
        '澳门特别行政区': {
          '澳门': ['花地玛堂区', '花王堂区', '望德堂区', '大堂区', '风顺堂区', '嘉模堂区', '路凼填海区', '圣方济各堂区']
        },
        '台湾省': {
          '台北市': ['中正区', '大同区', '中山区', '松山区', '大安区', '万华区', '信义区', '士林区', '北投区', '内湖区', '南港区', '文山区'],
          '新北市': ['板桥区', '三重区', '中和区', '永和区', '新庄区', '新店区', '土城区', '芦洲区', '树林区', '汐止区', '�的莺歌区', '三峡区', '淡水区', '瑞芳区'],
          '高雄市': ['楠梓区', '左营区', '鼓山区', '三民区', '盐埕区', '前金区', '新兴区', '苓雅区', '前镇区', '旗津区', '小港区', '凤山区', '林园区', '大寮区']
        }
      }
    }
  },
  computed: {
    provinces() {
      return Object.keys(this.regionData)
    },
    cities() {
      if (!this.form.province) return []
      return Object.keys(this.regionData[this.form.province] || {})
    },
    districts() {
      if (!this.form.province || !this.form.city) return []
      return this.regionData[this.form.province]?.[this.form.city] || []
    }
  },
  watch: {
    address: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          Object.assign(this.form, {
            receiverName: newVal.receiverName || '',
            phone: newVal.phone || '',
            province: newVal.province || '',
            city: newVal.city || '',
            district: newVal.district || '',
            detailAddress: newVal.detailAddress || '',
            postalCode: newVal.postalCode || ''
          });
        } else {
          this.resetForm();
        }
      }
    }
  },
  methods: {
    onProvinceChange() {
      this.form.city = ''
      this.form.district = ''
    },
    onCityChange() {
      this.form.district = ''
    },
    validatePhone(phone) {
      const phoneRegex = /^1[3-9]\d{9}$/
      return phoneRegex.test(phone)
    },
    handleSubmit() {
      this.phoneError = ''
      
      if (!this.validatePhone(this.form.phone)) {
        this.phoneError = '请输入正确的11位手机号码'
        return
      }
      
      if (!this.form.province || !this.form.city || !this.form.district) {
        alert('请选择完整的省市区')
        return
      }
      
      this.isSubmitting = true
      this.$emit('save', { ...this.form })
      // 保存成功后清除自动保存数据
      this.clearAutoSave()
      this.isSubmitting = false
    },
    resetForm() {
      Object.assign(this.form, {
        receiverName: '',
        phone: '',
        province: '',
        city: '',
        district: '',
        detailAddress: '',
        postalCode: ''
      });
      this.phoneError = '';
    }
  }
}
</script>

<style scoped>
.address-form {
  padding: 10px;
}

.address-form h3 {
  margin-bottom: 20px;
  text-align: center;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
}

.required {
  color: #f44336;
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: #4CAF50;
}

.region-selects {
  display: flex;
  gap: 10px;
}

.region-selects select {
  flex: 1;
}

.error-message {
  color: #f44336;
  font-size: 12px;
  margin-top: 5px;
  display: block;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.btn-primary {
  background-color: #4CAF50;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #45a049;
}

.btn-primary:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}
</style>
